import { z } from "zod";

export const storyblokAssetSchema = z
  .object({
    filename: z.string().optional(),
    alt: z.string().optional(),
    src: z.string().optional(),
  })
  .passthrough();

export type StoryblokAsset = z.infer<typeof storyblokAssetSchema>;

export const customisationFeatureSchema = z
  .object({
    heading: z.string(),
    body: z.string(),
    image: storyblokAssetSchema.optional(),
    imageFalse: storyblokAssetSchema.optional(),
    featureName: z.string(),
    firstToggleLabel: z.string(),
    secondToggleLabel: z.string(),
    trackingKey: z.string().optional(),
    toggleValues: z.string().optional(),
  })
  .passthrough();

export type CustomisationFeature = z.infer<typeof customisationFeatureSchema>;

export const stylePickerItemSchema = z
  .object({
    productId: z.union([z.string(), z.number()]),
    image: storyblokAssetSchema.optional(),
    label: z.string().optional(),
    trackingKey: z.string().optional(),
    toggleValues: z.string().optional(),
  })
  .passthrough();

export type StylePickerItem = z.infer<typeof stylePickerItemSchema>;

const stylePickerGroupSchema = z
  .object({
    items: z.array(stylePickerItemSchema).optional(),
    categoryPage: z.string().optional(),
  })
  .passthrough();

const comfortSuiteSettingsSchema = z
  .object({
    customisationFeatures: z.array(customisationFeatureSchema).optional(),
    stylePicker: z.array(stylePickerGroupSchema).optional(),
    featureTogglesExclude: z.array(z.string()).optional(),
  })
  .passthrough();

const parseArray = <T>(schema: z.ZodType<T>, value: unknown): T[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((item) => {
    const result = schema.safeParse(item);
    return result.success ? [result.data] : [];
  });
};

export const parseComfortSuiteSettings = (settings: unknown) => {
  const parsed = comfortSuiteSettingsSchema.safeParse(settings);
  if (parsed.success) {
    return {
      customisationFeatures: parsed.data.customisationFeatures ?? [],
      stylePickerItems: parsed.data.stylePicker?.[0]?.items ?? [],
      stylePickerGroups: parsed.data.stylePicker ?? [],
      featureTogglesExclude: parsed.data.featureTogglesExclude ?? [],
    };
  }

  const settingsRecord =
    typeof settings === "object" && settings !== null
      ? (settings as Record<string, unknown>)
      : {};
  const customisationFeatures = parseArray(
    customisationFeatureSchema,
    settingsRecord.customisationFeatures,
  );
  const stylePickerGroups = Array.isArray(settingsRecord.stylePicker)
    ? settingsRecord.stylePicker
    : [];
  const firstGroup =
    stylePickerGroups.length > 0 && typeof stylePickerGroups[0] === "object"
      ? (stylePickerGroups[0] as Record<string, unknown>)
      : undefined;
  const stylePickerItems = parseArray(stylePickerItemSchema, firstGroup?.items);
  const featureTogglesExclude = Array.isArray(
    settingsRecord.featureTogglesExclude,
  )
    ? (settingsRecord.featureTogglesExclude as string[])
    : [];

  return {
    customisationFeatures,
    stylePickerItems,
    stylePickerGroups: parseArray(stylePickerGroupSchema, stylePickerGroups),
    featureTogglesExclude,
  };
};

export const getStoryblokAssetUrl = (asset?: StoryblokAsset) =>
  asset?.filename ?? asset?.src;
